import { AlertTriangle } from "lucide-react";
import * as React from "react";
import { Component, ErrorInfo, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { errorTracker } from "@/lib/error-tracker";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("=== ERROR BOUNDARY CAUGHT ERROR ===");
    console.error("Error:", error);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);
    console.error("Component stack:", errorInfo.componentStack);
    console.error("React version:", React.version);
    console.error("===================================");
    
    // Track error with centralized error tracker
    errorTracker.track(error, {
      component: 'ErrorBoundary',
      metadata: {
        componentStack: errorInfo.componentStack,
        reactVersion: React.version
      }
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle>Noe gikk galt</CardTitle>
              </div>
              <CardDescription>
                En uventet feil oppstod i applikasjonen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="space-y-2">
                  <div className="p-3 bg-destructive/10 rounded-md">
                    <p className="text-sm font-semibold mb-1">Error Message:</p>
                    <p className="text-sm font-mono text-destructive break-all">
                      {this.state.error.message}
                    </p>
                  </div>
                  {this.state.error.stack && (
                    <div className="p-3 bg-muted rounded-md max-h-60 overflow-auto">
                      <p className="text-sm font-semibold mb-1">Stack Trace:</p>
                      <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
                    <p className="text-sm font-semibold mb-1">Debug Info:</p>
                    <p className="text-xs font-mono">React Version: {React.version}</p>
                    <p className="text-xs font-mono">Mode: {import.meta.env.MODE}</p>
                    <p className="text-xs font-mono mt-2 text-muted-foreground">
                      Sjekk browser console for mer detaljert informasjon
                    </p>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={this.handleReset} className="flex-1">
                  Gå til forsiden
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="flex-1"
                >
                  Last siden på nytt
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
